/**
 * @fileoverview CompanyLegalTab Component
 *
 * "Juridisch & Fiscaal" tab for the company detail page.
 * Displays and edits legal, fiscal, governance and personnel data
 * for a YallyHub vennootschap.
 */

import React from "react";
import { Card, CardBody, Input } from "@heroui/react";
import {
  CalendarBlank,
  IdentificationCard,
  Users,
  UserCircle,
  FirstAid,
  ChartPie,
  Crown,
  UsersThree,
} from "@phosphor-icons/react";
import { DatePickerInput } from "shared/components/DatePickerInput/DatePickerInput";
import DayMonthInput from "shared/components/DayMonthInput/DayMonthInput";
import GeneralMeetingDateInput from "shared/components/GeneralMeetingDateInput/GeneralMeetingDateInput";
import NacebelCodeInput from "./NacebelCodeInput";
import CompanyShareholderList from "./CompanyShareholderList";
import CompanyDirectorList from "./CompanyDirectorList";
import CompanyEmployeeList from "./CompanyEmployeeList";
import { ContactSearchSelect } from "screens/buildings/components/renovationTab/modals/components/ContactSearchSelect";
import { Contact } from "shared/types/contact";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanyLegalTabProps {
  company: any;
  onChange: (field: string, value: any) => void;
  isDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface CardSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const CardSection: React.FC<CardSectionProps> = ({ icon, title, children }) => (
  <Card shadow="none" className="overflow-visible border border-gray-200">
    <CardBody className="gap-3 overflow-visible p-3">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-neutral-primary">{title}</h3>
      </div>
      {children}
    </CardBody>
  </Card>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CompanyLegalTab: React.FC<CompanyLegalTabProps> = ({
  company,
  onChange,
  isDisabled = false,
}) => {
  const handleAuditorChange = (contact: Contact | null) => {
    onChange("auditorContactId", contact?.id ?? null);
    onChange("auditorContact", contact);
  };

  const handleMedicalSupervisorChange = (contact: Contact | null) => {
    onChange("medicalSupervisorContactId", contact?.id ?? null);
    onChange("medicalSupervisorContact", contact);
  };

  return (
    <div className="flex flex-col gap-3 py-3">
      {/* Card 1: Oprichtingsinfo */}
      <CardSection
        icon={<CalendarBlank className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Oprichtingsinfo"
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Oprichtingsdatum</span>
            <DatePickerInput
              value={company?.foundingDate || ""}
              onChange={(value) => onChange("foundingDate", value)}
              disabled={isDisabled}
              placeholder="Oprichtingsdatum"
            />
          </div>
          <Input
            label="Totaal aantal aandelen"
            type="number"
            value={company?.totalShares?.toString() || ""}
            onChange={(e) =>
              onChange("totalShares", e.target.value ? parseInt(e.target.value, 10) : null)
            }
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
        </div>
      </CardSection>

      {/* Card 2: Boekjaar */}
      <CardSection
        icon={<CalendarBlank className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Boekjaar"
      >
        <div className="grid grid-cols-2 gap-3">
          <DayMonthInput
            value={company?.fiscalYearStartDay || ""}
            onChange={(value) => onChange("fiscalYearStartDay", value)}
            isDisabled={isDisabled}
            label="Startdag boekjaar"
          />
          <DayMonthInput
            value={company?.fiscalYearEndDay || ""}
            onChange={(value) => onChange("fiscalYearEndDay", value)}
            isDisabled={isDisabled}
            label="Einddag boekjaar"
          />
        </div>
      </CardSection>

      {/* Card 3: Registratiecodes */}
      <CardSection
        icon={<IdentificationCard className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Registratiecodes"
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="LEI-code"
            type="text"
            value={company?.leiCode || ""}
            onChange={(e) => onChange("leiCode", e.target.value)}
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
          <Input
            label="Peppol-code"
            value={company?.peppolCode || ""}
            onChange={(e) => onChange("peppolCode", e.target.value)}
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
          <Input
            label="Paritair comité"
            value={company?.jointCommittee || ""}
            onChange={(e) => onChange("jointCommittee", e.target.value)}
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
          <Input
            label="RSZ nummer"
            value={company?.socialSecurityNumber || ""}
            onChange={(e) => onChange("socialSecurityNumber", e.target.value)}
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
          <Input
            label="Cevora"
            value={company?.cevora || ""}
            onChange={(e) => onChange("cevora", e.target.value)}
            variant="bordered"
            size="sm"
            isDisabled={isDisabled}
            classNames={{ label: "text-xs font-medium", input: "text-sm" }}
          />
          <div className="col-span-2">
            <p className="text-sm font-medium text-foreground mb-1.5">NACEBEL-codes</p>
            <NacebelCodeInput
              codes={company?.nacebelCodes || []}
              onChange={(codes) => onChange("nacebelCodes", codes)}
              isDisabled={isDisabled}
            />
          </div>
        </div>
      </CardSection>

      {/* Card 4: Algemene vergadering */}
      <CardSection
        icon={<Users className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Algemene vergadering"
      >
        <GeneralMeetingDateInput
          value={company?.generalMeetingDate || ""}
          onChange={(value) => onChange("generalMeetingDate", value)}
          isDisabled={isDisabled}
        />
      </CardSection>

      {/* Card 5: Commissaris */}
      <CardSection
        icon={<UserCircle className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Commissaris"
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-foreground">Commissaris</p>
            <ContactSearchSelect
              value={company?.auditorContact ?? null}
              onChange={handleAuditorChange}
              placeholder="Zoek commissaris..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">Start mandaat</span>
              <DatePickerInput
                value={company?.auditorMandateStartDate || ""}
                onChange={(value) => onChange("auditorMandateStartDate", value)}
                disabled={isDisabled}
                placeholder="Start mandaat"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">Einde mandaat</span>
              <DatePickerInput
                value={company?.auditorMandateEndDate || ""}
                onChange={(value) => onChange("auditorMandateEndDate", value)}
                disabled={isDisabled}
                placeholder="Einde mandaat"
              />
            </div>
          </div>
        </div>
      </CardSection>

      {/* Card 6: Verantwoordelijke medisch toezicht */}
      <CardSection
        icon={<FirstAid className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Verantwoordelijke medisch toezicht"
      >
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium text-foreground">Verantwoordelijke medisch toezicht</p>
          <ContactSearchSelect
            value={company?.medicalSupervisorContact ?? null}
            onChange={handleMedicalSupervisorChange}
            placeholder="Zoek verantwoordelijke..."
          />
        </div>
      </CardSection>

      {/* Card 7: Aandeelhouders */}
      <CardSection
        icon={<ChartPie className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Aandeelhouders"
      >
        <CompanyShareholderList
          shareholders={company?.shareholders || []}
          onChange={(shareholders) => onChange("shareholders", shareholders)}
          isDisabled={isDisabled}
        />
      </CardSection>

      {/* Card 8: Bestuurders */}
      <CardSection
        icon={<Crown className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Bestuurders"
      >
        <CompanyDirectorList
          directors={company?.directors || []}
          onChange={(directors) => onChange("directors", directors)}
          isDisabled={isDisabled}
        />
      </CardSection>

      {/* Card 9: Werknemers */}
      <CardSection
        icon={<UsersThree className="h-4 w-4 text-moss-primary" weight="duotone" />}
        title="Werknemers"
      >
        <CompanyEmployeeList
          employees={company?.employees || []}
          onChange={(employees) => onChange("employees", employees)}
          isDisabled={isDisabled}
        />
      </CardSection>
    </div>
  );
};

export default CompanyLegalTab;
